import React, { useState, useEffect } from 'react';
import { FaPlus, FaEdit, FaList, FaUserCheck } from 'react-icons/fa';

let url = process.env.REACT_APP_API_URL;

const API_URL = (() => {
  if (!url.endsWith('/api')) url = url.replace(/\/?$/, '/api');
  return url;
})();

const QUALIFICATIONS = ['10th pass', '12th pass', 'ITI', 'Diploma', 'Graduate'];

function AdminQuizDashboard({ onBack }) {
  const [quizzes, setQuizzes] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [newQuiz, setNewQuiz] = useState({ title: '', qualification: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showQuestions, setShowQuestions] = useState(false);
  const [selectedQuiz, setSelectedQuiz] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [newQuestion, setNewQuestion] = useState('');
  const [questionLoading, setQuestionLoading] = useState(false);
  const [questionError, setQuestionError] = useState('');
  const [showOptions, setShowOptions] = useState(null); // questionId
  const [newOption, setNewOption] = useState({ option_text: '', is_correct: false });
  const [assigning, setAssigning] = useState(false);
  const [assignMsg, setAssignMsg] = useState('');
  const [showEdit, setShowEdit] = useState(false);
  const [editQuiz, setEditQuiz] = useState({ id: null, title: '', qualification: '' });
  const [editError, setEditError] = useState('');
  const [editLoading, setEditLoading] = useState(false);

  useEffect(() => {
    fetchQuizzes();
  }, []);

  const fetchQuizzes = async () => {
    setLoading(true);
    const res = await fetch(`${API_URL}/quiz`);
    const data = await res.json();
    if (data.success) setQuizzes(data.quizzes);
    setLoading(false);
  };

  const handleCreateQuiz = async e => {
    e.preventDefault();
    setError('');
    if (!newQuiz.title || !newQuiz.qualification) return setError('All fields required');
    setLoading(true);
    const res = await fetch(`${API_URL}/quiz`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(newQuiz)
    });
    const data = await res.json();
    if (data.success) {
      setShowCreate(false);
      setNewQuiz({ title: '', qualification: '' });
      fetchQuizzes();
    } else {
      setError(data.message || 'Failed to create quiz');
    }
    setLoading(false);
  };

  const fetchQuestions = async quizId => {
    setQuestionLoading(true);
    const res = await fetch(`${API_URL}/quiz/${quizId}`);
    const data = await res.json();
    if (data.success) setQuestions(data.quiz.QuizQuestions || []);
    setQuestionLoading(false);
  };

  const handleOpenQuestions = async quiz => {
    setSelectedQuiz(quiz);
    setShowQuestions(true);
    await fetchQuestions(quiz.id);
  };

  const handleAddQuestion = async e => {
    e.preventDefault();
    setQuestionError('');
    if (!newQuestion) return setQuestionError('Enter question text');
    setQuestionLoading(true);
    const res = await fetch(`${API_URL}/quiz/${selectedQuiz.id}/question`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ question_text: newQuestion })
    });
    const data = await res.json();
    if (data.success) {
      setNewQuestion('');
      fetchQuestions(selectedQuiz.id);
    } else {
      setQuestionError(data.message || 'Failed to add question');
    }
    setQuestionLoading(false);
  };

  const handleDeleteQuestion = async questionId => {
    if (!window.confirm('Delete this question?')) return;
    setQuestionLoading(true);
    await fetch(`${API_URL}/quiz/question/${questionId}`, { method: 'DELETE' });
    fetchQuestions(selectedQuiz.id);
    setQuestionLoading(false);
  };

  const handleAddOption = async (questionId, e) => {
    e.preventDefault();
    if (!newOption.option_text) return;
    setQuestionLoading(true);
    await fetch(`${API_URL}/quiz/question/${questionId}/option`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(newOption)
    });
    setNewOption({ option_text: '', is_correct: false });
    fetchQuestions(selectedQuiz.id);
    setShowOptions(null);
    setQuestionLoading(false);
  };

  const handleDeleteOption = async optionId => {
    setQuestionLoading(true);
    await fetch(`${API_URL}/quiz/option/${optionId}`, { method: 'DELETE' });
    fetchQuestions(selectedQuiz.id);
    setQuestionLoading(false);
  };

  const handleAssignQuiz = async quiz => {
    setAssigning(true);
    setAssignMsg('');
    const res = await fetch(`${API_URL}/quiz/${quiz.id}/assign`, { method: 'POST' });
    const data = await res.json();
    if (data.success) {
      setAssignMsg('Quiz assigned to all matching candidates!');
    } else {
      setAssignMsg(data.message || 'Failed to assign quiz');
    }
    setAssigning(false);
    setTimeout(() => setAssignMsg(''), 3000);
  };

  const handleOpenEdit = quiz => {
    setEditQuiz({ id: quiz.id, title: quiz.title, qualification: quiz.qualification });
    setShowEdit(true);
    setEditError('');
  };

  const handleEditQuiz = async e => {
    e.preventDefault();
    setEditError('');
    if (!editQuiz.title || !editQuiz.qualification) return setEditError('All fields required');
    setEditLoading(true);
    const res = await fetch(`${API_URL}/quiz/${editQuiz.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ title: editQuiz.title, qualification: editQuiz.qualification })
    });
    const data = await res.json();
    if (data.success) {
      setShowEdit(false);
      fetchQuizzes();
    } else {
      setEditError(data.message || 'Failed to update quiz');
    }
    setEditLoading(false);
  };

  const handleDeleteQuiz = async quizId => {
    if (!window.confirm('Delete this quiz? All questions and assignments will be removed.')) return;
    setLoading(true);
    await fetch(`${API_URL}/quiz/${quizId}`, { method: 'DELETE' });
    fetchQuizzes();
    setLoading(false);
  };

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #e0f7fa 0%, #f8fafc 100%)', padding: 32 }}>
      <div style={{ maxWidth: 900, margin: '0 auto' }}>
        {/* Back Button */}
        {onBack && (
          <button className="gogig-card-btn-outline" style={{ marginBottom: 18, borderRadius: 32, padding: '10px 32px', fontWeight: 700, fontSize: 16, color: '#22c55e', border: '2px solid #22c55e', background: 'rgba(255,255,255,0.85)', transition: 'all 0.18s', boxShadow: '0 2px 8px #22c55e11', letterSpacing: 0.5 }} onClick={onBack}>&larr; Back</button>
        )}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 32 }}>
          <h2 style={{ fontWeight: 900, fontSize: 32, color: '#22c55e', letterSpacing: 1 }}>Quiz Management</h2>
          <button className="gogig-card-btn" onClick={() => setShowCreate(true)} style={{ borderRadius: 32, padding: '14px 32px', fontWeight: 800, fontSize: 18, background: 'linear-gradient(90deg, #22c55e 0%, #2ad2c9 100%)', color: '#fff', boxShadow: '0 2px 16px #22c55e44', border: 'none', letterSpacing: 0.5, transition: 'background 0.18s, transform 0.18s', display: 'flex', alignItems: 'center', gap: 12 }}><FaPlus /> Create Quiz</button>
        </div>
        <div style={{ background: 'rgba(255,255,255,0.85)', borderRadius: 24, boxShadow: '0 8px 32px #22c55e22', border: '1.5px solid #e0f7fa', overflow: 'hidden' }}>
          <div style={{ padding: 18, borderBottom: '1.5px solid #e0f7fa', fontWeight: 700, color: '#22c55e', display: 'grid', gridTemplateColumns: '2fr 1fr 2fr', gap: 16, background: 'linear-gradient(90deg, #e0f7fa 0%, #f8fafc 100%)', fontSize: 17, letterSpacing: 0.5 }}>
            <div>Title</div>
            <div>Qualification</div>
            <div>Actions</div>
          </div>
          {loading ? (
            <div style={{ padding: 32, textAlign: 'center' }}>Loading...</div>
          ) : (
            quizzes.map((quiz, idx) => (
              <div key={quiz.id} style={{ padding: 18, borderBottom: '1.5px solid #e0f7fa', display: 'grid', gridTemplateColumns: '2fr 1fr 2fr', gap: 16, alignItems: 'center', background: idx % 2 === 0 ? 'rgba(34,197,94,0.06)' : 'rgba(42,210,201,0.06)', transition: 'background 0.2s', cursor: 'pointer' }} onMouseOver={e => e.currentTarget.style.background='rgba(34,197,94,0.13)'} onMouseOut={e => e.currentTarget.style.background=idx%2===0?'rgba(34,197,94,0.06)':'rgba(42,210,201,0.06)'}>
                <div style={{ fontWeight: 600, color: '#222', fontSize: 16 }}>{quiz.title}</div>
                <div style={{ fontWeight: 700, color: '#22c55e', fontSize: 15 }}>{quiz.qualification}</div>
                <div style={{ display: 'flex', gap: 12 }}>
                  <button className="gogig-card-btn-outline" onClick={() => handleOpenEdit(quiz)} style={{ borderRadius: 32, padding: '10px 22px', fontWeight: 700, fontSize: 15, color: '#22c55e', border: '2px solid #22c55e', background: 'rgba(255,255,255,0.85)', transition: 'all 0.18s', boxShadow: '0 2px 8px #22c55e11', letterSpacing: 0.5, display: 'flex', alignItems: 'center', gap: 8 }}><FaEdit /> Edit</button>
                  <button className="gogig-card-btn-outline" onClick={() => handleOpenQuestions(quiz)} style={{ borderRadius: 32, padding: '10px 22px', fontWeight: 700, fontSize: 15, color: '#22c55e', border: '2px solid #22c55e', background: 'rgba(255,255,255,0.85)', transition: 'all 0.18s', boxShadow: '0 2px 8px #22c55e11', letterSpacing: 0.5, display: 'flex', alignItems: 'center', gap: 8 }}><FaList /> Questions</button>
                  <button className="gogig-card-btn-outline" onClick={() => handleAssignQuiz(quiz)} disabled={assigning} style={{ borderRadius: 32, padding: '10px 22px', fontWeight: 700, fontSize: 15, color: '#22c55e', border: '2px solid #22c55e', background: 'rgba(255,255,255,0.85)', transition: 'all 0.18s', boxShadow: '0 2px 8px #22c55e11', letterSpacing: 0.5, display: 'flex', alignItems: 'center', gap: 8 }}><FaUserCheck /> Assign</button>
                  <button className="gogig-card-btn-outline" style={{ color: 'red', borderRadius: 32, padding: '10px 22px', fontWeight: 700, fontSize: 15, border: '2px solid #ef4444', background: 'rgba(255,255,255,0.85)', transition: 'all 0.18s', boxShadow: '0 2px 8px #ef444422', letterSpacing: 0.5 }} onClick={() => handleDeleteQuiz(quiz.id)}>Delete</button>
                </div>
              </div>
            ))
          )}
        </div>
        {assignMsg && <div style={{ marginTop: 18, color: '#22c55e', textAlign: 'center', fontWeight: 600 }}>{assignMsg}</div>}
      </div>
      {/* Create Quiz Modal */}
      {showCreate && (
        <div className="gogig-modal-bg" onClick={() => setShowCreate(false)}>
          <div className="gogig-modal" onClick={e => e.stopPropagation()} style={{ minWidth: 340, background: 'rgba(255,255,255,0.95)', borderRadius: 24, boxShadow: '0 8px 32px #22c55e22', border: '1.5px solid #e0f7fa', padding: 32 }}>
            <h3 style={{ marginBottom: 18, fontWeight: 800, color: '#22c55e', fontSize: 22 }}>Create New Quiz</h3>
            <form onSubmit={handleCreateQuiz} style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
              <input className="gogig-input" placeholder="Quiz Title" value={newQuiz.title} onChange={e => setNewQuiz({ ...newQuiz, title: e.target.value })} style={{ borderRadius: 16, border: '1.5px solid #e0f7fa', padding: '12px 18px', fontSize: 16 }} />
              <select
                className="gogig-input"
                value={newQuiz.qualification}
                onChange={e => setNewQuiz({ ...newQuiz, qualification: e.target.value })}
                style={{ borderRadius: 16, border: '1.5px solid #e0f7fa', padding: '12px 18px', fontSize: 16 }}
              >
                <option value="">Select Qualification</option>
                {QUALIFICATIONS.map(q => <option key={q} value={q}>{q}</option>)}
              </select>
              {error && <div style={{ color: 'red', textAlign: 'center' }}>{error}</div>}
              <button className="gogig-card-btn" type="submit" disabled={loading} style={{ borderRadius: 32, padding: '14px 0', fontWeight: 800, fontSize: 18, background: 'linear-gradient(90deg, #22c55e 0%, #2ad2c9 100%)', color: '#fff', boxShadow: '0 2px 16px #22c55e44', border: 'none', width: '100%', letterSpacing: 1, transition: 'background 0.18s, transform 0.18s' }}>{loading ? 'Creating...' : 'Create'}</button>
            </form>
          </div>
        </div>
      )}
      {/* Questions Modal */}
      {showQuestions && (
        <div className="gogig-modal-bg" onClick={() => setShowQuestions(false)}>
          <div className="gogig-modal" onClick={e => e.stopPropagation()} style={{ minWidth: 500, maxHeight: '80vh', overflowY: 'auto', background: 'rgba(255,255,255,0.97)', borderRadius: 24, boxShadow: '0 8px 32px #22c55e22', border: '1.5px solid #e0f7fa', padding: 32 }}>
            <h3 style={{ marginBottom: 18, fontWeight: 800, color: '#22c55e', fontSize: 22 }}>Questions for: <span style={{ color: '#2ad2c9' }}>{selectedQuiz?.title}</span></h3>
            <form onSubmit={handleAddQuestion} style={{ display: 'flex', gap: 10, marginBottom: 18 }}>
              <input className="gogig-input" placeholder="Add question..." value={newQuestion} onChange={e => setNewQuestion(e.target.value)} style={{ borderRadius: 16, border: '1.5px solid #e0f7fa', padding: '12px 18px', fontSize: 16 }} />
              <button className="gogig-card-btn" type="submit" disabled={questionLoading} style={{ borderRadius: 32, padding: '12px 24px', fontWeight: 800, fontSize: 16, background: 'linear-gradient(90deg, #22c55e 0%, #2ad2c9 100%)', color: '#fff', boxShadow: '0 2px 16px #22c55e44', border: 'none', letterSpacing: 0.5, transition: 'background 0.18s, transform 0.18s' }}>Add</button>
            </form>
            {questionError && <div style={{ color: 'red', marginBottom: 8 }}>{questionError}</div>}
            {questionLoading ? <div>Loading...</div> : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
                {questions.map(q => (
                  <div key={q.id} style={{ background: 'rgba(34,197,94,0.06)', borderRadius: 18, padding: 20, border: '1.5px solid #e0f7fa', marginBottom: 4, boxShadow: '0 2px 12px #22c55e11' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                      <div style={{ fontWeight: 800, color: '#222', fontSize: 17, letterSpacing: 0.2 }}>{q.question_text}</div>
                      <button className="gogig-card-btn-outline" onClick={() => handleDeleteQuestion(q.id)} style={{ color: '#ef4444', borderRadius: 22, padding: '6px 18px', fontWeight: 700, fontSize: 15, border: '2px solid #ef4444', background: '#fff', transition: 'all 0.18s', boxShadow: '0 2px 8px #ef444422', letterSpacing: 0.5, marginLeft: 16 }}>Delete</button>
                    </div>
                    <div style={{ marginTop: 2 }}>
                      <strong style={{ color: '#22c55e', fontWeight: 800, fontSize: 15, letterSpacing: 0.2, marginBottom: 8, display: 'block' }}>Options:</strong>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 8 }}>
                        {(q.QuizOptions || []).map(opt => (
                          <div key={opt.id} style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#fff', borderRadius: 22, padding: '8px 18px', boxShadow: '0 1px 6px #22c55e11', border: '1.5px solid #e0f7fa', fontWeight: 600, fontSize: 15, color: '#222', marginBottom: 2 }}>
                            <span>{opt.option_text}</span>
                            {opt.is_correct && <span style={{ color: '#22c55e', fontWeight: 800, background: 'rgba(34,197,94,0.13)', borderRadius: 10, padding: '2px 10px', marginLeft: 4, fontSize: 13 }}>Correct</span>}
                            <button className="gogig-card-btn-outline" style={{ color: '#ef4444', borderRadius: 18, padding: '2px 12px', fontSize: 13, border: '1.5px solid #ef4444', background: '#fff', marginLeft: 4, fontWeight: 700, transition: 'all 0.18s' }} onClick={() => handleDeleteOption(opt.id)}>Delete</button>
                          </div>
                        ))}
                      </div>
                      {showOptions === q.id ? (
                        <form onSubmit={e => handleAddOption(q.id, e)} style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                          <input className="gogig-input" placeholder="Option text" value={newOption.option_text} onChange={e => setNewOption({ ...newOption, option_text: e.target.value })} style={{ borderRadius: 14, border: '1.5px solid #e0f7fa', padding: '10px 14px', fontSize: 15 }} />
                          <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontWeight: 600, color: '#22c55e' }}>
                            <input type="checkbox" checked={newOption.is_correct} onChange={e => setNewOption({ ...newOption, is_correct: e.target.checked })} /> Correct
                          </label>
                          <button className="gogig-card-btn" type="submit" style={{ borderRadius: 22, padding: '10px 18px', fontWeight: 700, fontSize: 15, background: 'linear-gradient(90deg, #22c55e 0%, #2ad2c9 100%)', color: '#fff', boxShadow: '0 2px 8px #22c55e44', border: 'none', letterSpacing: 0.5, transition: 'background 0.18s, transform 0.18s' }}>Add</button>
                          <button className="gogig-card-btn-outline" type="button" onClick={() => setShowOptions(null)} style={{ borderRadius: 22, padding: '10px 18px', fontWeight: 700, fontSize: 15, color: '#22c55e', border: '2px solid #22c55e', background: '#fff', marginLeft: 2 }}>Cancel</button>
                        </form>
                      ) : (
                        <button className="gogig-card-btn" style={{ marginTop: 8, borderRadius: 22, padding: '10px 24px', fontWeight: 700, fontSize: 16, background: 'linear-gradient(90deg, #22c55e 0%, #2ad2c9 100%)', color: '#fff', boxShadow: '0 2px 8px #22c55e44', border: 'none', letterSpacing: 0.5, transition: 'background 0.18s, transform 0.18s', display: 'flex', alignItems: 'center', gap: 8 }} onClick={() => setShowOptions(q.id)}>+ Add Option</button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
      {/* Edit Quiz Modal */}
      {showEdit && (
        <div className="gogig-modal-bg" onClick={() => setShowEdit(false)}>
          <div className="gogig-modal" onClick={e => e.stopPropagation()} style={{ minWidth: 340, background: 'rgba(255,255,255,0.95)', borderRadius: 24, boxShadow: '0 8px 32px #22c55e22', border: '1.5px solid #e0f7fa', padding: 32 }}>
            <h3 style={{ marginBottom: 18, fontWeight: 800, color: '#22c55e', fontSize: 22 }}>Edit Quiz</h3>
            <form onSubmit={handleEditQuiz} style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
              <input className="gogig-input" placeholder="Quiz Title" value={editQuiz.title} onChange={e => setEditQuiz({ ...editQuiz, title: e.target.value })} style={{ borderRadius: 16, border: '1.5px solid #e0f7fa', padding: '12px 18px', fontSize: 16 }} />
              <select
                className="gogig-input"
                value={editQuiz.qualification}
                onChange={e => setEditQuiz({ ...editQuiz, qualification: e.target.value })}
                style={{ borderRadius: 16, border: '1.5px solid #e0f7fa', padding: '12px 18px', fontSize: 16 }}
              >
                <option value="">Select Qualification</option>
                {QUALIFICATIONS.map(q => <option key={q} value={q}>{q}</option>)}
              </select>
              {editError && <div style={{ color: 'red', textAlign: 'center' }}>{editError}</div>}
              <button className="gogig-card-btn" type="submit" disabled={editLoading} style={{ borderRadius: 32, padding: '14px 0', fontWeight: 800, fontSize: 18, background: 'linear-gradient(90deg, #22c55e 0%, #2ad2c9 100%)', color: '#fff', boxShadow: '0 2px 16px #22c55e44', border: 'none', width: '100%', letterSpacing: 1, transition: 'background 0.18s, transform 0.18s' }}>{editLoading ? 'Saving...' : 'Save'}</button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminQuizDashboard; 